import type { HttpErrorResponse } from '@angular/common/http';

import {
  createEntityCollectionStore,
  getEntityCollectionActions,
  ngrxEvent,
  pipeState,
} from '@recruitee/ngrx';
import type {
  OpenQuestionsTemplate,
  SaveQuestionPayload,
  TemplateOpenQuestionsItem,
} from '@recruitee/open-questions-templates-types';

const { actions, selectors, reducer, namespace } =
  createEntityCollectionStore<OpenQuestionsTemplate>()('OpenQuestionsTemplatesJobTemplate', {
    // eslint-disable-next-line max-lines-per-function
    customActions: adapter => {
      const reducerFunctions = getEntityCollectionActions(
        adapter,
        ({ id }: { id?: number }) => id!,
      );

      return {
        fetchCollection: (state, payload: { jobTemplateId: number; scope: string }) =>
          reducerFunctions.fetchCollection(state, payload),
        fetchCollectionSuccess: (
          state,
          payload: { data: OpenQuestionsTemplate[]; scope: string },
        ) =>
          pipeState(
            state,
            next => adapter.upsertMany(next, payload.data, { scope: payload.scope }),
            next =>
              adapter.markAsLoaded(next, {
                scope: payload.scope,
                entities: payload.data.map(e => e.id),
              }),
          ),
        fetchCollectionFailed: (state, payload: { error: HttpErrorResponse }) =>
          reducerFunctions.fetchCollectionFailed(state, payload),

        fetchOne: (state, payload: { jobTemplateId: number; templateId: number; scope: string }) =>
          adapter.markAsPending(state),
        fetchOneSuccess: (state, payload: { data: OpenQuestionsTemplate; scope: string }) =>
          pipeState(
            state,
            next => {
              if (!payload.data.custom) return adapter.addOne(next, payload.data);

              const jobTemplateIds = next.scope[payload.scope] || [];
              const currentCustomTemplateId = jobTemplateIds.find(templateId => {
                const template = next.entityMap[templateId];
                return template?.custom;
              });

              return currentCustomTemplateId
                ? adapter.replaceOne(next, currentCustomTemplateId, payload.data)
                : adapter.addOne(next, payload.data);
            },
            next =>
              adapter.markAsLoaded(next, {
                entities: [payload.data.id],
                scope: payload.scope,
              }),
          ),

        assignTemplate: ngrxEvent<{
          jobTemplateId: number;
          templateId?: number;
          scope: string;
        }>(),
        assignTemplateSuccess: ngrxEvent<{
          data: OpenQuestionsTemplate;
          scope: string;
        }>(),

        saveAsTemplate: (
          state,
          payload: { title: string; category: string; jobTemplateId: number },
        ) => adapter.markAsPending(state),
        saveAsTemplateSuccess: (state, payload: OpenQuestionsTemplate) =>
          pipeState(state, next => adapter.markAsLoaded(next)),
        saveAsTemplateFailed: (state, payload: HttpErrorResponse) =>
          adapter.setError(state, payload),

        saveQuestion: ngrxEvent<{ data: SaveQuestionPayload }>(),
        saveQuestionSuccess: ngrxEvent<{ templateId: number }>(),
        saveQuestionFail: ngrxEvent<{ error: string[] }>(),

        deleteQuestion: ngrxEvent<{
          templateId: number;
          questionId: number;
          jobTemplateId: number;
        }>(),
        deleteQuestionSuccess: ngrxEvent<void>(),
        deleteQuestionFail: ngrxEvent<{ error: string[] }>(),

        removeOnePessimistic: (
          state,
          payload: { templateId: string; questionId: string; jobTemplateId: number },
        ) => adapter.markAsPending(state, { entities: [payload.questionId], scope: null }),
        removeOnePessimisticSuccess: (state, payload: { questionId: string }) =>
          adapter.removeOne(state, payload.questionId),

        updateQuestionsPositions: ngrxEvent<{
          templateId: number;
          positions: number[];
          jobTemplateId: number;
        }>(),
        updateQuestionsPositionsSuccess: ngrxEvent<{ data: TemplateOpenQuestionsItem[] }>(),
        updateQuestionsPositionsFail: ngrxEvent<{ error: string[] }>(),
      };
    },
  });

export const getOfferTemplateScope = (jobTemplateId: number) => `offer-template:${jobTemplateId}`;

export {
  actions as OpenQuestionsTemplatesJobTemplateActions,
  selectors as OpenQuestionsTemplatesJobTemplateSelectors,
  reducer as OpenQuestionsTemplatesJobTemplateReducer,
  namespace as OpenQuestionsTemplatesJobTemplateNamespace,
};
